<script setup>
import IEventItem from "@components/IEventItem/IEventItem.vue";
import ITeams from "@components/ITeams/ITeams.vue";
import { translator } from "@shop/frontend/web/js/services/TranslatorService";
import EventsItemsEmpty from "./EventsItemsEmpty.vue";
import EventItemsSkeleton from "./EventItemsSkeleton.vue";
import useSiteStore, { blockingTypeEnum } from "@vueApp/store/modules/site";
import { computed, onMounted, watch } from "vue";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/ru';

dayjs.extend(utc);
dayjs.locale('ru');

const LOGO_ICON_URL = '/images/site/logo.svg';

const store = useSiteStore();

store.searchEvents();
store.searchSubscriptions();
store.searchServices();
store.searchShows();
store.searchPerformances();

const loadingData = computed(
	() =>
		store.getSubscriptionsState.loading ||
		store.getEventsState.loading ||
		store.getServicesState.loading ||
		store.getPerformancesState.loading
);

const showEvents = computed(
	() =>
		!!store.getEventsState.items.length &&
		(!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(blockingTypeEnum.MATCH))
);

const showPerformances = computed(
	() =>
		!!store.getPerformancesState.items.length &&
		(!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(blockingTypeEnum.PERFORMANCE))
);

const showSubscriptions = computed(
	() =>
		!!store.getSubscriptionsState.items.length &&
		(!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(blockingTypeEnum.SUBSCRIPTION))
);

const showServices = computed(
	() =>
		!!store.getServicesState.items.length &&
		(!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(blockingTypeEnum.SERVICE_TAGLESS))
);

const showShows = computed(
	() =>
		!!store.getShowsState.items.length &&
		(!store.getSelectTypes.items.length || store.getSelectTypes.items.includes(5))
);

watch(loadingData, () => (loadingData.value ? $("#js-overlay").show() : $("#js-overlay").hide()));

</script>

<template>
	<event-items-skeleton :is-data-loading="loadingData">
		<events-items-empty>
			<div v-if="showEvents" class="event-item">
				<h3 class="event-item__title" data-test-id="matches_title">{{ translator.t("main", "Matches") }}</h3>
				<i-event-item v-for="event in store.getEventsState.items" :key="event.id" :event="event"
					:isPackage="event.eventType === 'Пакет'">
					<template #name v-if="event.eventType !== 'Пакет'">
						<i-teams :first-team="event.participants.team?.homeTeam || event.participants.singular
							" :second-team="event.participants.team?.guestTeam" :to="!event.settings.isSoon ? event.pageLink : ''"
							:is-home-logo-with-opponent="event.settings.isHomeLogoWithOpponent" :name="event.name"
							class="teams_main"></i-teams>
					</template>
					<template v-else #package>
						<div class="event-item__package-events">
							<i-event-item v-for="eventItem in event.packageEvents" :key="eventItem.id" :event="eventItem"
								:is-package="eventItem.eventType === 'Пакет'" :is-package-item="true">
								<template v-if="eventItem.eventType !== 'Пакет'" #name>
									<i-teams :first-team="eventItem.participants.team?.homeTeam || eventItem.participants.singular
									" :second-team="eventItem.participants.team?.guestTeam"
										:to="!eventItem.settings.isSoon ? eventItem.pageLink : ''"
										:is-home-logo-with-opponent="eventItem.settings.isHomeLogoWithOpponent"
										:is-home-logo-without-opponent="eventItem.settings.isHomeLogoWithoutOpponent"
										:is-show-tournament="eventItem.settings.isShowTournament" :name="eventItem.name"
										class="teams_main" />
								</template>
							</i-event-item>
						</div>
					</template>
				</i-event-item>
				<div v-if="store.getEventsState.showLoadMore" class="text-center">
					<button class="btn btn_border" data-test-id="matches_load_btn" @click="store.loadMoreEvents">
						{{ translator.t("main", "More") }}
					</button>
				</div>
			</div>

			<div v-if="showSubscriptions" class="event-item">
				<h3 class="event-item__title" data-test-id="season_tickets_title">
					{{ translator.t("main", "Season tickets") }}
				</h3>
				<i-event-item v-for="event in store.getSubscriptionsState.items" :key="event.id"
					:event="event"></i-event-item>
				<div v-if="store.getSubscriptionsState.showLoadMore" class="text-center">
					<button class="btn btn_border" data-test-id="season_tickets_load_btn"
						@click="store.loadMoreSubscriptions">
						{{ translator.t("main", "More") }}
					</button>
				</div>
			</div>

			<div v-if="showServices" class="event-item">
				<h3 class="event-item__title" data-test-id="services_title">{{ translator.t("event", "Services") }}</h3>
				<div class="event-item__cols-container">
					<div v-for="event in store.getServicesState.items" :key="event.id">
						<i-event-item :event="event" is-short>
							<template #name>
								<div class="d-flex align-items-center w-100">
									<div class="i-event-item__image">
										<img :src="event.imagePath || LOGO_ICON_URL" :alt="event.name" width="82"
											height="110" />
									</div>
									<div>
										<div class="i-event-item__info-wrapper">
											<span class="i-event-item__age-limit">
												{{ event.ageLimit }}+
											</span>
											<div v-if="event.hasPushkinPay">
												<svg width="24" height="24" class="icon">
													<use xlink:href="#pushkin-in-square-border-radius"></use>
												</svg>
											</div>
										</div>
										<div class="i-event-item__name">{{ event.name }}</div>
										<span class="i-event-item__min-price">
											{{ translator.t("main", "from ") }}
											{{ event.minPrice }} ₽
										</span>
									</div>
								</div>
							</template>
						</i-event-item>
					</div>
				</div>
				<div v-if="store.getServicesState.showLoadMore" class="text-center">
					<button class="btn btn_border" data-test-id="services_load_btn" @click="store.loadMoreServices">
						{{ translator.t("main", "More") }}
					</button>
				</div>
			</div>

			<div v-if="showPerformances" class="event-item">
				<h3 class="event-item__title" data-test-id="events_title">{{ translator.t("event", "Concerts") }}</h3>
				<i-event-item v-for="event in store.getPerformancesState.items" :key="event.id" :event="event"
					:is-perfomance="true">
					<template #info>
						<div class="i-event-item__perfomance-info-wrapper">
							<a :href="event.pageLink">
								<div class="i-event-item__image">
									<span v-if="event.hasPushkinPay" class="i-event-item__pushkin-icon">
										<svg width="24" height="24" class="icon">
											<use xlink:href="#pushkin"></use>
										</svg>
									</span>
									<img :src="event.participants.singular.imageUrl || LOGO_ICON_URL"
										:alt="event.participants.singular.name" width="82" height="110" />
								</div>
							</a>
							<div class="d-flex d-sm-none i-event-item__perfomance-info-right">
								<span class="i-event-item__age-limit">
									{{ event.ageLimit }}+
								</span>
								<a :href="event.pageLink" class="i-event-item__name">
									{{ event.participants.singular.name }}
								</a>
								<div class="i-event-item-info__date-wrapper">
									<div v-if="event.settings.isShowDate"
										class="d-inline-flex align-items-center i-event-item-info__date">
										<span>
											{{ dayjs(event.startTimestampStr).format('DD MMMM, dddd HH:mm') }}
										</span>
									</div>
								</div>
							</div>
						</div>
					</template>
					<template #name>
						<div class="d-flex flex-column align-items-sm-center">
							<div class="d-none d-sm-flex align-items-center">
								<span class="i-event-item__age-limit">{{ event.ageLimit }}+</span>
								<a :href="event.pageLink" class="i-event-item__name">
									{{ event.participants.singular.name }}
								</a>
							</div>
							<div class="i-event-item-info__date-wrapper d-none d-sm-flex">
								<div v-if="event.settings.isShowDate"
									class="d-inline-flex align-items-center text-nowrap i-event-item-info__date">
									<span>
										{{ dayjs(event.startTimestampStr).format('DD MMMM, dddd HH:mm') }}
									</span>
								</div>
							</div>
						</div>
					</template>
				</i-event-item>
				<div v-if="store.getPerformancesState.showLoadMore" class="text-center">
					<button class="btn btn_border" data-test-id="events_load_btn" @click="store.loadMorePerformances">
						{{ translator.t("main", "More") }}
					</button>
				</div>
			</div>
		</events-items-empty>
	</event-items-skeleton>
</template>

<style lang="scss">
.event-item {
	display: flex;
	flex-direction: column;
	gap: 24px;

	&__title {
		margin-bottom: 0;
		font-size: 36px;
		font-family: 'Impact', sans-serif;
		margin-top: 44px;
		text-transform: uppercase;
	}

	&__cols-container {
		display: grid;
		grid-template-columns: 1fr 1fr;
		column-gap: 20px;
		row-gap: 24px;
	}

	&__package-events {
		display: flex;
		flex-direction: column;
		gap: 16px;
		background-color: rgba(197, 196, 196, 0.07);
		backdrop-filter: blur(11.6px);
		margin-top: 20px;
		border-radius: $border-radius;
	}

	@media (max-width: $screen-md) {
		&__title {
			font-size: 32px;
			margin-top: 32px;
		}

		&__title_small {
			content: '';
			bottom: 3px;
			left: 2px;
			font-weight: 600;
			font-size: 20px;
		}

		&__package-events {
			gap: 8px;
		}
	}

	@media (max-width: $screen-xs) {
		&__cols-container {
			grid-template-columns: 1fr;
		}
	}
}
</style>
